/* eslint-disable max-len */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Container, Row, Card, CardBody } from 'reactstrap';
import classNames from 'classnames';
import ReactMarkdown from 'lib/react-markdown';
import getContent from 'lib/getContent';
import { useBootstrapBreakpoint } from 'components/VirtualMentorChat/utils/mobileUtils';
import NextLiveEventSectionSkeleton from './NextLiveEventSection.skeleton';
import liveEventCardBg from 'images/bg/wlc-live-event-card-bg.jpg';
import wlcLogoFallback from 'images/wlc-logo.png';

const DEFAULT_EVENT_SLUG = 'wlc-next-live-event';
const LIVE_EVENTS_LOGO_URL = 'https://cdn.foodrevolution.org/members/assets/logos/wlc-live-events-logo-white.png';

const resolveAssetSrc = asset => {
  if (!asset) return '';
  return typeof asset === 'string' ? asset : asset.default || '';
};

const liveEventCardBgSrc = resolveAssetSrc(liveEventCardBg);
const wlcLogoFallbackSrc = resolveAssetSrc(wlcLogoFallback);

const toNumberTimestamp = value => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const mapContentToEvent = contentItem => {
  if (!contentItem) return null;

  const title = contentItem.displayProps?.title || contentItem.title || contentItem.name || '';
  const date = toNumberTimestamp(
    contentItem.displayProps?.date || contentItem.data?.time || contentItem.data?.startTime
  );
  const image = contentItem.displayProps?.image || contentItem.data?.image || contentItem.data?.thumb || null;
  const description = contentItem.displayProps?.subtitle || contentItem.data?.description || '';
  const registerLink = contentItem.data?.joinUrl || null;

  return {
    title,
    date,
    image,
    description,
    registerLink
  };
};

const PurpleCard = ({ children }) => (
  <Card className="background-dark-purple text-white" style={{ height: '100%' }}>
    <CardBody>{children}</CardBody>
  </Card>
);

const NextLiveEventSection = ({
  authToken,
  productSlug = 'wlc',
  eventSlug = DEFAULT_EVENT_SLUG,
  liveEventsHubLink,
  showEventsList = true
}) => {
  const [nextLiveEvent, setNextLiveEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const breakpoint = useBootstrapBreakpoint();

  const isMobile = breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md';

  useEffect(() => {
    let isMounted = true;

    const fetchEvent = async () => {
      if (!authToken || !productSlug) {
        return;
      }

      setIsLoading(true);
      try {
        const eventContent = await getContent(eventSlug, productSlug, authToken, '?depth=1');
        if (isMounted) setNextLiveEvent(mapContentToEvent(eventContent));
      } catch (error) {
        console.error('Error fetching next live event:', error);
        if (isMounted) setNextLiveEvent(null);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchEvent();

    return () => {
      isMounted = false;
    };
  }, [authToken, productSlug, eventSlug]);

  const effectiveLiveEventsHubLink = liveEventsHubLink || nextLiveEvent?.registerLink;

  if (isLoading && !nextLiveEvent) {
    return <NextLiveEventSectionSkeleton showEventsList={showEventsList} />;
  }

  if (!nextLiveEvent && !effectiveLiveEventsHubLink) {
    return null;
  }

  const eventsList = [
    'In the Kitchen',
    'Table Topics',
    'Questions & Answers',
    'Community Connections',
    'Member Support'
  ];

  const formatEventDate = timestamp => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const EventHubWrapperTag = isMobile ? 'div' : PurpleCard;
  const NextLiveEventWrapperTag = isMobile ? 'div' : PurpleCard;
  const NextLiveEventInnerCardWrapperTag = isMobile ? Card : 'div';

  return (
    <div className={classNames('py-5', isMobile ? 'background-dark-purple' : 'background-light-gray')}>
      <Container>
        <Row className="justify-content-center mb-4">
          <Col xs={12} lg={nextLiveEvent ? 6 : 12} className="text-center">
            <EventHubWrapperTag>
              <div
                className="mb-4"
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${liveEventCardBgSrc})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  aspectRatio: '1755 / 1080',
                  width: '100%',
                  maxWidth: '600px',
                  margin: '0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1rem'
                }}
              >
                <img
                  src={LIVE_EVENTS_LOGO_URL}
                  alt="WHOLE Life Club Live Events"
                  onError={event => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = wlcLogoFallbackSrc;
                  }}
                  style={{ maxWidth: '400px', width: '100%', height: 'auto', display: 'block' }}
                />
              </div>

              {effectiveLiveEventsHubLink && (
                <Button
                  href={effectiveLiveEventsHubLink}
                  size="lg"
                  className="mb-4"
                  tag="a"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Upcoming Events
                </Button>
              )}

              {showEventsList && (
                <div className="text-white text-left" style={{ maxWidth: '600px', margin: '0 auto' }}>
                  <p className="mb-3">
                    This will take you to your Live Events Hub where you can join new live events and watch recordings
                    of past events, including:
                  </p>
                  <ul className="mb-0">
                    {eventsList.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </EventHubWrapperTag>
          </Col>
          {nextLiveEvent && (
            <Col xs={12} lg={6}>
              <NextLiveEventWrapperTag>
                <h5 className="text-white mb-4 mt-lg-0">Your Next Live Event:</h5>
                <NextLiveEventInnerCardWrapperTag>
                  {nextLiveEvent.image && (
                    <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', overflow: 'hidden' }}>
                      <img
                        src={nextLiveEvent.image.url}
                        alt={nextLiveEvent.image.alt || nextLiveEvent.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  )}

                  <CardBody className="p-4">
                    {nextLiveEvent.title && <h5 className="mb-2 mt-0">{nextLiveEvent.title}</h5>}

                    {nextLiveEvent.hosts && <p className="text-muted mb-3">with {nextLiveEvent.hosts}</p>}

                    {nextLiveEvent.date && <h5 className="mb-3">{formatEventDate(nextLiveEvent.date)}</h5>}

                    {nextLiveEvent.description && (
                      <div className="mb-4">
                        <ReactMarkdown>{nextLiveEvent.description}</ReactMarkdown>
                      </div>
                    )}

                    {nextLiveEvent.registerLink && (
                      <Button
                        href={nextLiveEvent.registerLink}
                        size="lg"
                        block
                        tag="a"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Register Here
                      </Button>
                    )}
                  </CardBody>
                </NextLiveEventInnerCardWrapperTag>
              </NextLiveEventWrapperTag>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
};

NextLiveEventSection.propTypes = {
  authToken: PropTypes.string,
  productSlug: PropTypes.string,
  eventSlug: PropTypes.string,
  liveEventsHubLink: PropTypes.string,
  showEventsList: PropTypes.bool
};

NextLiveEventSection.defaultProps = {
  authToken: null,
  productSlug: 'wlc',
  eventSlug: DEFAULT_EVENT_SLUG,
  liveEventsHubLink: null,
  showEventsList: true
};

export default NextLiveEventSection;
